import React, { useState } from 'react'
import type {
  ShallowTaskWorkflowMilestone,
  WorkflowResource,
  ReoOffer,
  ReoContractMediaFile,
} from '@adc/sdk/types'
import * as graph from '@adc/sdk'
import Button from '../../elementals/button'
import DownloadLink from '../../downloadLink'
import TrackingModal from '../MilestoneTracking'
import classnames from 'classnames/bind'
import styles from '../styles.scss'

const cx = classnames.bind(styles)

const MEDIA_TYPE_BY_MILESTONE_CODE = {
  CONTRACT_RECEIVED_FROM_SELLER: 'INTERNAL_SELLER_SIGNED_CONTRACT',
  SIGNED_SALES_CONTRACT: 'INTERNAL_SIGNED_CONTRACT',
  EARNEST_MONEY_DEPOSIT_UPLOADED: 'EMD',
  PROOF_OF_FUNDS_UPLOADED: 'POF',
}

type MilestoneMediaCode = keyof typeof MEDIA_TYPE_BY_MILESTONE_CODE

const DownloadLinkAsButton = ({
  resource,
}: {
  resource: ReoContractMediaFile
}) => (
  <DownloadLink
    variant='button'
    buttonProps={{
      role: 'default',
      size: 'small',
      elmId: 'view_signed_contract_btn',
    }}
    resource={resource}
  >
    View
  </DownloadLink>
)

const isReoOffer = (resource: WorkflowResource): resource is ReoOffer => {
  return (
    'media' in resource &&
    'latest_contract' in resource &&
    'listing_id' in resource
  )
}

interface MilestoneActionProps<T extends WorkflowResource = WorkflowResource> {
  milestone: ShallowTaskWorkflowMilestone
  resource: T
  isDisabled?: boolean
}

const MilestoneAction = <T extends WorkflowResource = WorkflowResource>({
  milestone,
  resource,
  isDisabled,
}: MilestoneActionProps<T>) => {
  const [showTrackingModal, setShowTrackingModal] = useState(false)

  const onShowTrackingModalClick = () => {
    setShowTrackingModal(true)
  }

  const onCloseTrackingModalClick = () => {
    setShowTrackingModal(false)
  }

  if (!isReoOffer(resource)) {
    return null
  }

  const closing = resource as ReoOffer

  if (milestone.code in MEDIA_TYPE_BY_MILESTONE_CODE) {
    const mediaType =
      MEDIA_TYPE_BY_MILESTONE_CODE[milestone.code as MilestoneMediaCode]
    const relevantMedia =
      closing?.media?.find(m => m.media_type.includes(mediaType)) ||
      closing?.latest_contract?.media?.find(m =>
        m.media_type.includes(mediaType)
      )
    if (milestone.code === 'SIGNED_SALES_CONTRACT') {
      //TODO filter signerrecipients
      const docusignTracking =
        closing?.latest_contract?.tracking?.docusign_tracking

      if (!docusignTracking) {
        return null
      }

      return (
        <div className={cx('milestone-action-buttons')}>
          <Button
            role='default'
            size='small'
            elmId='purchased_contract_tracking_btn'
            onClick={onShowTrackingModalClick}
            disabled={
              isDisabled ||
              !(
                (docusignTracking.docusign_recipient_tracking
                  ?.length as number) > 0
              )
            }
          >
            Track
          </Button>
          {relevantMedia && <DownloadLinkAsButton resource={relevantMedia} />}
          {(docusignTracking?.docusign_recipient_tracking?.length as number) >
            0 && (
            <TrackingModal
              isOpen={showTrackingModal}
              title='Purchase Contract Signatures'
              onCloseModal={onCloseTrackingModalClick}
              people={docusignTracking?.docusign_recipient_tracking || []}
            />
          )}
        </div>
      )
    }

    if (relevantMedia) {
      return <DownloadLink resource={relevantMedia} />
    }
  }

  if (milestone.code === 'OFFER_DATA_COMPLETED') {
    if (
      milestone.status === graph.enums.ShallowTaskWorkflowItemStatus.Complete
    ) {
      return null
    }

    return (
      <Button
        role='secondary'
        size='small'
        rel='noopener noreferrer'
        target='_blank'
        link={`/contract/${closing?.listing_id}/contract/contact`}
        elmId={'contract_info_form_button'}
      >
        Start
      </Button>
    )
  }

  return null
}

export default MilestoneAction
